import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Input,
  InputNumber,
  Select,
  message,
  Tag,
  Card,
  Statistic,
  Row,
  Col,
  Popconfirm,
  Typography,
  Tooltip,
  Alert
} from 'antd';
import {
  DeleteOutlined,
  WarningOutlined,
  BellOutlined,
  ReloadOutlined,
  ExperimentOutlined,
  SettingOutlined,
  SaveOutlined,
  AlertOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
  GlobalOutlined,
  PlusOutlined
} from '@ant-design/icons';
import PageLayout from './common/PageLayout';
import { config } from '../config';
import { useSimpleStore } from '../stores/simpleStore';

const { Text } = Typography;
const { TextArea } = Input;

// 긴급 단계별 태그 설정
const EMRG_STEP_CONFIG: Record<string, { color: string; icon: React.ReactNode }> = {
  '안전안내': { color: 'blue', icon: <SafetyCertificateOutlined /> },
  '긴급재난': { color: 'orange', icon: <AlertOutlined /> },
  '위급재난': { color: 'red', icon: <ThunderboltOutlined /> }
};

interface DisasterAlert {
  SN: string;
  MSG_CN: string;
  RCPTN_RGN_NM: string | null;
  CRT_DT: string | null;
  EMRG_STEP_NM: string | null;
  DST_SE_NM: string | null;
  REG_YMD: string | null;
  MDFCN_YMD: string | null;
  created_at: string;
  hidden_at: string | null;
}

interface DisasterStats {
  total: number;
  safety_count: number;
  emergency_count: number;
  critical_count: number;
  active_count: number;
}

interface DisasterSettings {
  polling_interval_seconds: number;
  region_filter: string;
  global_region_filter: string;
  company_id: number | null;
}

export default function DisasterManagement() {
  const [alerts, setAlerts] = useState<DisasterAlert[]>([]);
  const [stats, setStats] = useState<DisasterStats>({ total: 0, safety_count: 0, emergency_count: 0, critical_count: 0, active_count: 0 });
  const [loading, setLoading] = useState(false);
  const [emrgStepFilter, setEmrgStepFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [detailModal, setDetailModal] = useState<DisasterAlert | null>(null);

  // 수동 추가 모달
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addForm, setAddForm] = useState({
    emrg_step: '안전안내' as string,
    msg_cn: '',
    region: '전국',
    dst_se: '수동등록',
    duration_minutes: 30
  });
  const [addLoading, setAddLoading] = useState(false);

  // 설정
  const [pollingInterval, setPollingInterval] = useState(10);
  const [regionFilter, setRegionFilter] = useState('');
  const [globalRegionFilter, setGlobalRegionFilter] = useState('');
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsChanged, setSettingsChanged] = useState(false);
  const [globalSettingsChanged, setGlobalSettingsChanged] = useState(false);

  // 운수사 선택기 연동
  const { selectedCompanyId, selectedCompanyName } = useSimpleStore();

  const token = localStorage.getItem('adminToken');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // 목록 로드
  const loadAlerts = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${config.apiBaseUrl}/api/disaster/list?page=${page}&limit=20`;
      if (emrgStepFilter) url += `&emrg_step=${encodeURIComponent(emrgStepFilter)}`;

      const response = await fetch(url, { headers });
      const data = await response.json();
      if (data.success) {
        setAlerts(data.data.alerts);
        setStats(data.data.stats);
        setTotal(data.data.pagination.total);
      } else {
        message.error(data.error || '목록 로드 실패');
      }
    } catch (error) {
      console.error('재난정보 목록 로드 에러:', error);
      message.error('재난정보 목록 로드 중 오류 발생');
    } finally {
      setLoading(false);
    }
  }, [page, emrgStepFilter]);

  // 설정 로드 (운수사별)
  const loadSettings = useCallback(async () => {
    try {
      let url = `${config.apiBaseUrl}/api/disaster/settings`;
      if (selectedCompanyId) url += `?company_id=${selectedCompanyId}`;

      const response = await fetch(url, { headers });
      const data = await response.json();
      if (data.success) {
        setPollingInterval(data.data.polling_interval_seconds);
        setRegionFilter(data.data.region_filter || '');
        setGlobalRegionFilter(data.data.global_region_filter || '');
        setSettingsChanged(false);
        setGlobalSettingsChanged(false);
      }
    } catch (error) {
      console.error('설정 로드 에러:', error);
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    loadAlerts();
    loadSettings();
  }, [loadAlerts, loadSettings]);

  // 30초마다 자동 새로고침
  useEffect(() => {
    const interval = setInterval(loadAlerts, 30000);
    return () => clearInterval(interval);
  }, [loadAlerts]);

  // 운수사별 설정 저장
  const handleSaveCompanySettings = async () => {
    if (!selectedCompanyId) {
      message.warning('운수사를 먼저 선택해주세요');
      return;
    }
    setSettingsLoading(true);
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/disaster/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          company_id: selectedCompanyId,
          region_filter: regionFilter
        })
      });
      const data = await response.json();
      if (data.success) {
        message.success(`${selectedCompanyName} 지역 필터가 저장되었습니다`);
        setSettingsChanged(false);
      } else {
        message.error(data.error || '설정 저장 실패');
      }
    } catch (error) {
      message.error('설정 저장 중 오류 발생');
    } finally {
      setSettingsLoading(false);
    }
  };

  // 글로벌 설정 저장 (폴링 간격 + 기본 지역 필터)
  const handleSaveGlobalSettings = async () => {
    setSettingsLoading(true);
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/disaster/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          polling_interval_seconds: pollingInterval,
          region_filter: globalRegionFilter
        })
      });
      const data = await response.json();
      if (data.success) {
        message.success('글로벌 설정이 저장되었습니다');
        setGlobalSettingsChanged(false);
      } else {
        message.error(data.error || '설정 저장 실패');
      }
    } catch (error) {
      message.error('설정 저장 중 오류 발생');
    } finally {
      setSettingsLoading(false);
    }
  };

  // 삭제
  const handleDelete = async (sn: string) => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/disaster/${encodeURIComponent(sn)}`, {
        method: 'DELETE',
        headers
      });
      const data = await response.json();
      if (data.success) {
        message.success('삭제되었습니다');
        loadAlerts();
      } else {
        message.error(data.error || '삭제 실패');
      }
    } catch (error) {
      message.error('삭제 중 오류 발생');
    }
  };

  // 테스트 재난정보 생성
  const handleTestAlert = async (emrgStep: string) => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/disaster/test`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ emrg_step: emrgStep })
      });
      const data = await response.json();
      if (data.success) {
        message.success(data.message);
        loadAlerts();
      } else {
        message.error(data.error || '테스트 생성 실패');
      }
    } catch (error) {
      message.error('테스트 생성 중 오류 발생');
    }
  };

  // 수동 재난정보 추가
  const handleAddAlert = async () => {
    if (!addForm.msg_cn.trim()) {
      message.warning('메시지 내용을 입력해주세요');
      return;
    }
    setAddLoading(true);
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/disaster/test`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...addForm, region: selectedCompanyName || '전국' })
      });
      const data = await response.json();
      if (data.success) {
        message.success(data.message);
        setAddModalVisible(false);
        setAddForm({ emrg_step: '안전안내', msg_cn: '', region: '전국', dst_se: '수동등록', duration_minutes: 30 });
        loadAlerts();
      } else {
        message.error(data.error || '추가 실패');
      }
    } catch (error) {
      message.error('재난정보 추가 중 오류 발생');
    } finally {
      setAddLoading(false);
    }
  };

  // 활성 여부 확인
  const isActive = (alert: DisasterAlert) => {
    if (!alert.hidden_at) return false;
    return new Date(alert.hidden_at) > new Date();
  };

  // 테이블 컬럼
  const columns = [
    {
      title: 'SN',
      dataIndex: 'SN',
      key: 'SN',
      width: 120,
      ellipsis: true,
      render: (sn: string) => <Text copyable={{ text: sn }}>{sn.substring(0, 12)}...</Text>
    },
    {
      title: '긴급단계',
      dataIndex: 'EMRG_STEP_NM',
      key: 'EMRG_STEP_NM',
      width: 110,
      render: (step: string) => {
        const cfg = EMRG_STEP_CONFIG[step] || { color: 'default', icon: <BellOutlined /> };
        return <Tag color={cfg.color} icon={cfg.icon}>{step || '미분류'}</Tag>;
      }
    },
    {
      title: '재해구분',
      dataIndex: 'DST_SE_NM',
      key: 'DST_SE_NM',
      width: 100
    },
    {
      title: '내용',
      dataIndex: 'MSG_CN',
      key: 'MSG_CN',
      ellipsis: true,
      render: (text: string, record: DisasterAlert) => (
        <Tooltip title="클릭하여 전문 보기">
          <a onClick={() => setDetailModal(record)} style={{ color: 'inherit' }}>
            {text}
          </a>
        </Tooltip>
      )
    },
    {
      title: '수신지역',
      dataIndex: 'RCPTN_RGN_NM',
      key: 'RCPTN_RGN_NM',
      width: 150,
      ellipsis: true
    },
    {
      title: '발생시각',
      dataIndex: 'CRT_DT',
      key: 'CRT_DT',
      width: 160
    },
    {
      title: '상태',
      key: 'status',
      width: 80,
      render: (_: any, record: DisasterAlert) => (
        isActive(record)
          ? <Tag color="red">활성</Tag>
          : <Tag color="default">만료</Tag>
      )
    },
    {
      title: '작업',
      key: 'actions',
      width: 80,
      render: (_: any, record: DisasterAlert) => (
        <Popconfirm
          title="정말 삭제하시겠습니까?"
          onConfirm={() => handleDelete(record.SN)}
          okText="삭제"
          cancelText="취소"
          okButtonProps={{ danger: true }}
        >
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      )
    }
  ];

  return (
    <PageLayout
      title="재난정보 관리"
      subtitle="행정안전부 재난문자를 자동 수집하고 단말기에 전송합니다"
    >
      {/* 글로벌 설정 카드 */}
      <Card
        size="small"
        title={<><GlobalOutlined /> 글로벌 설정 (전체 운수사 공통)</>}
        style={{ marginBottom: 16 }}
      >
        <Space wrap>
          <span>폴링 간격:</span>
          <InputNumber
            min={60}
            max={600}
            value={pollingInterval}
            onChange={(v) => {
              setPollingInterval(v || 10);
              setGlobalSettingsChanged(true);
            }}
            addonAfter="초"
            style={{ width: 120 }}
          />
          <span>기본 지역 필터:</span>
          <Input
            value={globalRegionFilter}
            onChange={(e) => {
              setGlobalRegionFilter(e.target.value);
              setGlobalSettingsChanged(true);
            }}
            placeholder="예: 서울 (운수사별 미설정 시 사용)"
            style={{ width: 280 }}
          />
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveGlobalSettings}
            loading={settingsLoading}
            disabled={!globalSettingsChanged}
          >
            글로벌 설정 저장
          </Button>
        </Space>
      </Card>

      {/* 운수사별 지역 필터 카드 */}
      <Card
        size="small"
        title={
          <><SettingOutlined /> 운수사별 지역 필터 {selectedCompanyName && <Tag color="blue">{selectedCompanyName}</Tag>}</>
        }
        style={{ marginBottom: 16 }}
      >
        {!selectedCompanyId ? (
          <Alert
            message="좌측 메뉴에서 운수사를 선택하면 해당 운수사의 지역 필터를 설정할 수 있습니다."
            type="info"
            showIcon
          />
        ) : (
          <Space wrap>
            <span>지역 필터:</span>
            <Input
              value={regionFilter}
              onChange={(e) => {
                setRegionFilter(e.target.value);
                setSettingsChanged(true);
              }}
              placeholder="예: 서울, 경기 (비워두면 글로벌 설정 사용)"
              style={{ width: 300 }}
            />
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSaveCompanySettings}
              loading={settingsLoading}
              disabled={!settingsChanged}
            >
              {selectedCompanyName} 설정 저장
            </Button>
            {regionFilter ? (
              <Text type="secondary">이 운수사 단말기는 "{regionFilter}" 지역 재난만 표시합니다</Text>
            ) : (
              <Text type="secondary">글로벌 기본 필터 사용 중: "{globalRegionFilter || '전체'}"</Text>
            )}
          </Space>
        )}
      </Card>

      {/* 통계 카드 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6} md={5}>
          <Card size="small">
            <Statistic title="전체" value={stats.total} prefix={<BellOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6} md={5}>
          <Card size="small">
            <Statistic title="활성 (30분 이내)" value={stats.active_count} valueStyle={{ color: '#cf1322' }} prefix={<WarningOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6} md={5}>
          <Card size="small">
            <Statistic title="안전안내" value={stats.safety_count} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6} md={5}>
          <Card size="small">
            <Statistic title="긴급재난" value={stats.emergency_count} valueStyle={{ color: '#fa8c16' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Card size="small">
            <Statistic title="위급재난" value={stats.critical_count} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
      </Row>

      {/* 도구 모음 */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <Space wrap>
          <Select
            style={{ width: 160 }}
            placeholder="긴급단계 필터"
            allowClear
            value={emrgStepFilter || undefined}
            onChange={(value) => { setEmrgStepFilter(value || ''); setPage(1); }}
          >
            <Select.Option value="안전안내">안전안내</Select.Option>
            <Select.Option value="긴급재난">긴급재난</Select.Option>
            <Select.Option value="위급재난">위급재난</Select.Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={loadAlerts}>새로고침</Button>
        </Space>
        <Space wrap>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalVisible(true)}>
            재난정보 추가
          </Button>
          <Button icon={<ExperimentOutlined />} onClick={() => handleTestAlert('안전안내')}>
            테스트: 안전안내
          </Button>
          <Button icon={<ExperimentOutlined />} danger onClick={() => handleTestAlert('긴급재난')}>
            테스트: 긴급재난
          </Button>
        </Space>
      </div>

      {/* 테이블 */}
      <Table
        columns={columns}
        dataSource={alerts}
        rowKey="SN"
        loading={loading}
        pagination={{
          current: page,
          pageSize: 20,
          total: total,
          onChange: (p) => setPage(p),
          showTotal: (t) => `전체 ${t}건`
        }}
        scroll={{ x: 1100 }}
        rowClassName={(record) => isActive(record) ? 'ant-table-row-active' : ''}
      />

      {/* 상세 모달 */}
      <Modal
        title={
          <Space>
            {detailModal?.EMRG_STEP_NM && (
              <Tag color={EMRG_STEP_CONFIG[detailModal.EMRG_STEP_NM]?.color || 'default'}>
                {detailModal.EMRG_STEP_NM}
              </Tag>
            )}
            <span>재난정보 상세</span>
          </Space>
        }
        open={!!detailModal}
        onCancel={() => setDetailModal(null)}
        footer={<Button onClick={() => setDetailModal(null)}>닫기</Button>}
        width={600}
      >
        {detailModal && (
          <div>
            <p><strong>SN:</strong> {detailModal.SN}</p>
            <p><strong>재해구분:</strong> {detailModal.DST_SE_NM || '-'}</p>
            <p><strong>수신지역:</strong> {detailModal.RCPTN_RGN_NM || '-'}</p>
            <p><strong>발생시각:</strong> {detailModal.CRT_DT || '-'}</p>
            <p><strong>숨김시각:</strong> {detailModal.hidden_at || '-'}</p>
            <Card style={{ marginTop: 16, backgroundColor: '#f5f5f5' }}>
              <p style={{ whiteSpace: 'pre-wrap', fontSize: 16 }}>{detailModal.MSG_CN}</p>
            </Card>
          </div>
        )}
      </Modal>

      {/* 수동 추가 모달 */}
      <Modal
        title={<><PlusOutlined /> 재난정보 수동 추가</>}
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        onOk={handleAddAlert}
        confirmLoading={addLoading}
        okText="추가"
        cancelText="취소"
        width={600}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <Text strong>긴급단계</Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              value={addForm.emrg_step}
              onChange={(v) => setAddForm({ ...addForm, emrg_step: v })}
            >
              <Select.Option value="안전안내">안전안내 (하단 자막)</Select.Option>
              <Select.Option value="긴급재난">긴급재난 (전체화면 + 빨간 테두리)</Select.Option>
              <Select.Option value="위급재난">위급재난 (전체화면 + 빨간 테두리)</Select.Option>
            </Select>
          </div>
          <div>
            <Text strong>메시지 내용 <Text type="danger">*</Text></Text>
            <TextArea
              style={{ marginTop: 4 }}
              rows={4}
              value={addForm.msg_cn}
              onChange={(e) => setAddForm({ ...addForm, msg_cn: e.target.value })}
              placeholder="단말기에 표시될 재난 안내 메시지를 입력하세요"
              maxLength={500}
              showCount
            />
          </div>
          <Row gutter={16}>
            <Col span={12}>
              <Text strong>수신지역</Text>
              <Input
                style={{ marginTop: 4 }}
                value={selectedCompanyName || '전국'}
                disabled
              />
            </Col>
            <Col span={12}>
              <Text strong>재해구분</Text>
              <Input
                style={{ marginTop: 4 }}
                value={addForm.dst_se}
                onChange={(e) => setAddForm({ ...addForm, dst_se: e.target.value })}
                placeholder="예: 호우, 지진, 수동등록"
              />
            </Col>
          </Row>
          <div>
            <Text strong>표시 시간</Text>
            <InputNumber
              style={{ width: '100%', marginTop: 4 }}
              min={1}
              max={1440}
              value={addForm.duration_minutes}
              onChange={(v) => setAddForm({ ...addForm, duration_minutes: v || 30 })}
              addonAfter="분"
            />
            <Text type="secondary" style={{ fontSize: 12 }}>설정한 시간이 지나면 자동으로 숨김 처리됩니다 (최대 24시간)</Text>
          </div>
        </div>
      </Modal>

      <style>{`
        .ant-table-row-active {
          background-color: #fff1f0 !important;
        }
        .ant-table-row-active:hover > td {
          background-color: #ffccc7 !important;
        }
      `}</style>
    </PageLayout>
  );
}
